/**
 * The email Razorpay's Create Payment API is given when the payer has none.
 *
 * ## Why this is needed
 *
 * `POST /v1/payments/create/checkout` rejects a blank email outright:
 *
 *   BAD_REQUEST_ERROR — "Could not validate payment create request due to:
 *   email: cannot be blank." (reason: input_validation_failed)
 *
 * Every phone-only account has `contactEmail = null` by design — the identity
 * split made "no email" a real, representable state rather than a placeholder —
 * so omitting the field failed 100% of those checkouts, on web and native alike.
 *
 * ## Why this particular address
 *
 * `void@razorpay.com` is the placeholder Razorpay uses in its own SDK samples
 * (e.g. react-native-razorpay's `prefill`). It is NOT a magic sentinel — the API
 * gives it no special handling; it is simply a well-known, syntactically valid
 * address on a domain Razorpay controls. That is the whole property being relied
 * on: the field is non-blank, and nothing is ever delivered to a stranger.
 *
 * Do not read it as "Razorpay knows this means no email". It doesn't. If a
 * receipt is generated it goes to Razorpay's own domain and dies there, which is
 * the correct outcome for a payer who has given us no address.
 *
 * Using the account's auth email instead would be the bug this codebase already
 * guards against: for a phone signup that column holds a random
 * `@phone.hiive.invalid` value, which is an identity credential, not an address.
 * See `lib/identity/contact-email.ts`.
 *
 * ## Where this must NOT be used
 *
 * Two places, both load-bearing:
 *
 * 1. **Anything shown to a person** — the payer summary, receipts, the profile.
 *    Those must keep rendering "no email", because that is the truth, and
 *    because the checkout offers to collect a real one.
 * 2. **Razorpay Standard Checkout `prefill`** — its modal collects an email when
 *    the field is blank, so seeding the sentinel would suppress the one moment a
 *    payer would have handed us a real address. Only Custom Checkout, where we
 *    build the entire request ourselves and there is no UI to collect anything,
 *    needs it.
 */
export const RAZORPAY_PLACEHOLDER_EMAIL = "void@razorpay.com";

/**
 * The email to send with a Custom Checkout `createPayment` request.
 *
 * Pass the payer's verified contact address; get back either that address or the
 * sentinel. Never returns undefined — a blank value is precisely what Razorpay
 * refuses.
 */
export function razorpayPaymentEmail(
  contactEmail: string | null | undefined,
): string {
  const normalized = contactEmail?.trim();
  return normalized ? normalized : RAZORPAY_PLACEHOLDER_EMAIL;
}
